import {
  type Limits,
  type MetricUsage,
  type ProjectQuotaLimits,
  type UsageMetricMetadata,
  type MessageTypeOptions,
  type Page,
  APIError,
  handleApiError,
  pageNavigator,
  type ApiErrorHandler,
  CommonAPI,
  PlaygroundSeed,
  PlaygroundRunPayload,
  PlaygroundRunResult,
  SessionStats,
  SessionTokenUsage,
  SessionXAxis,
  SessionRange,
  SessionTreeNode,
  AgentCloneTree,
  AgencyTagType,
  RatingType,
  RateMomentResponse,
} from "@bagel-web/common";

import type {
  AgentDetails,
  AgentPresentation,
  AgentState,
  APIKey,
  APIRequestDetails,
  APIRequestSummary,
  CreateM2MCredentialRequest,
  EntitlementAuditList,
  EntitlementStatus,
  CreateOIDCIssuerRequest,
  EditProject,
  M2MCredentialList,
  M2MCredentialWithSecret,
  SetStoresRequest,
  StoreCredentialRequest,
  OIDCIssuer,
  OIDCIssuerList,
  NewProject,
  MessageStreamMomentDetail,
  MessageStreamMomentSummary,
  Organization,
  Project,
  SessionDetails,
  SessionSummary,
  SessionUsage,
  DashboardUser,
  DashboardProjectUser,
  AddProjectUser,
  AgentMomentList,
  AuthorizedServiceAlias,
  OrganizationService,
  EditOrganizationService,
  DashboardProjectSummary,
} from "./types";

function buildQueryString(
  obj: Record<
    string,
    | string
    | number
    | boolean
    | Array<string | number | boolean | undefined>
    | undefined
  >
) {
  const params: Array<string> = [];

  Object.keys(obj)
    .filter((k) => obj[k] !== undefined && obj[k] !== null)
    .forEach((k) => {
      const v = obj[k];

      if (Array.isArray(v)) {
        v.forEach((item) => {
          const encodedValue =
            item !== null && item !== undefined ? encodeURIComponent(item) : "";
          params.push(`${encodeURIComponent(k)}=${encodedValue}`);
        });
      } else {
        const encodedValue =
          v !== null && v !== undefined ? encodeURIComponent(v) : "";
        params.push(`${encodeURIComponent(k)}=${encodedValue}`);
      }
    });

  return params.join("&");
}

async function parseError(res: Response): Promise<APIError> {
  try {
    const json = await res.json();

    if ("error" in json) {
      return new APIError({
        error: json.error,
        status: res.status,
        headers: res.headers,
      });
    }
  } catch (e) {
    // fall back to a generic error.
  }

  return new APIError({
    error: {
      type: "unknown",
      message: `Unexpected response status ${res.status}.`,
      trace: res.headers.get("X-trace-id") ?? "",
    },
    status: res.status,
    headers: res.headers,
  });
}

// Dashboard-only: a 409 carrying target org/project headers means the requested
// resource lives in a different organization. Redirect through the org switch
// (which sets the active org/project) and keep the request pending so no error
// flashes first. Everything else falls through to the common handler (401
// reload, otherwise re-throw).
const handleDashboardApiError = (error: APIError): Promise<never> => {
  const switchOrganization: ApiErrorHandler = (e) => {
    if (e.status !== 409) return false;
    const targetOrganizationId = e.headers?.get("X-Target-Organization-Id");
    const targetProjectId = e.headers?.get("X-Target-Project-Id");
    if (!targetOrganizationId || !targetProjectId) return false;
    const currentRoute = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    pageNavigator.navigateTo(
      `/switch-organization/${targetOrganizationId}?showPrompt=true&projectId=${targetProjectId}&path=${encodeURIComponent(currentRoute)}`
    );
    return true;
  };

  return handleApiError(error, switchOrganization);
};

export async function getAPI<T>(url: string): Promise<T> {
  const res = await fetch(url);

  if (res.status === 200) return res.json();
  else return handleDashboardApiError(await parseError(res));
}

export async function postAPI<T>(
  url: string,
  body: object,
  headers?: object
): Promise<T> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...headers },
    body: JSON.stringify(body),
  });

  if (res.status === 200) return res.json();
  else return handleDashboardApiError(await parseError(res));
}

export async function patchAPI<T>(
  url: string,
  body: object,
  init?: { signal?: AbortSignal | null }
): Promise<T> {
  return fetch(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    ...init,
  }).then((res) => {
    if (res.status === 200) return res.json();
    else throw new Error(`Unexpected response status ${res.status}`);
  });
}

export async function putAPI<T>(
  url: string,
  body: object,
  headers?: object
): Promise<T | undefined> {
  const res = await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...headers },
    body: JSON.stringify(body),
  });

  if (res.ok) {
    try {
      return await res.json();
    } catch (e) {
      return undefined;
    }
  } else return handleDashboardApiError(await parseError(res));
}

export async function deleteAPI<T>(
  url: string,
  init?: { signal?: AbortSignal | null }
): Promise<T | undefined> {
  const res = await fetch(url, { method: "DELETE", ...init });
  if (res.ok) {
    try {
      return await res.json();
    } catch (e) {
      return undefined;
    }
  } else return handleDashboardApiError(await parseError(res));
}

export async function fetchOrganization(): Promise<Organization> {
  return getAPI("/api/organization");
}

export async function fetchProjects({
  includeArchived = false,
  startsAfter = null,
}: {
  includeArchived?: boolean;
  startsAfter?: string | null;
}): Promise<Page<DashboardProjectSummary>> {
  return getAPI(
    "/api/projects?" +
      buildQueryString({
        include_archived: includeArchived || undefined,
        starting_after: startsAfter || undefined,
      })
  );
}

export async function fetchProject({
  projectId,
}: {
  projectId: string;
}): Promise<Project> {
  return getAPI(`/api/projects/${projectId}`);
}

export async function createProject({
  project,
}: {
  project: NewProject;
}): Promise<Project> {
  const url = `/api/projects`;
  return postAPI<Project>(url, project);
}

export async function patchProject({
  project,
}: {
  project: EditProject;
}): Promise<void> {
  const url = `/api/projects/${project.id}`;
  return patchAPI(url, project);
}

export async function archiveProject({
  project,
}: {
  project: EditProject;
}): Promise<void> {
  const url = `/api/projects/${project.id}/archive`;
  return postAPI(url, {});
}

export async function unarchiveProject({
  project,
}: {
  project: EditProject;
}): Promise<void> {
  const url = `/api/projects/${project.id}/unarchive`;
  return postAPI(url, {});
}

export async function fetchUsers(): Promise<Page<DashboardUser>> {
  return getAPI("/api/users");
}

export async function fetchProjectUsers({
  projectId,
}: {
  projectId: string;
}): Promise<Page<DashboardProjectUser>> {
  return getAPI(`/api/projects/${projectId}/users`);
}

export async function addUserToProject({
  projectId,
  user,
}: {
  projectId: string;
  user: AddProjectUser;
}): Promise<DashboardProjectUser> {
  return postAPI(`/api/projects/${projectId}/users`, user);
}

export async function removeUserFromProject({
  projectId,
  userId,
}: {
  projectId: string;
  userId: string;
}): Promise<void> {
  await deleteAPI(`/api/projects/${projectId}/users/${userId}`);
}

export async function fetchAPIKeys({
  startsAfter = null,
  notes,
}: {
  startsAfter: string | null;
  notes?: string;
}): Promise<Page<APIKey>> {
  return getAPI(
    "/api/api-keys?" +
      buildQueryString({
        starting_after: startsAfter || undefined,
        notes: notes || undefined,
      })
  );
}

// BAGEL-312: dashboard M2M credentials. The create + rotate calls
// return ``M2MCredentialWithSecret`` (the one-time client_secret
// that the UI is responsible for surfacing to the studio and then
// forgetting). List returns the parent ``M2MCredential`` shape
// with no client_secret field at all.
export async function fetchM2MCredentials({
  projectId,
}: {
  projectId: string;
}): Promise<M2MCredentialList> {
  return getAPI(`/api/projects/${projectId}/m2m-credentials`);
}

export async function createM2MCredential({
  projectId,
  body,
}: {
  projectId: string;
  body: CreateM2MCredentialRequest;
}): Promise<M2MCredentialWithSecret> {
  return postAPI(`/api/projects/${projectId}/m2m-credentials`, body);
}

export async function revokeM2MCredential({
  projectId,
  credentialId,
}: {
  projectId: string;
  credentialId: string;
}): Promise<void> {
  await deleteAPI(`/api/projects/${projectId}/m2m-credentials/${credentialId}`);
}

export async function rotateM2MCredential({
  projectId,
  credentialId,
}: {
  projectId: string;
  credentialId: string;
}): Promise<M2MCredentialWithSecret> {
  return postAPI(
    `/api/projects/${projectId}/m2m-credentials/${credentialId}/rotate`,
    {}
  );
}

// BAGEL-448: self-serve OIDC issuer registration. Sister surface to
// the M2M credentials calls above; rows produced here have
// workos_application_id NULL and are filtered out of the M2M list.
export async function fetchOIDCIssuers({
  projectId,
}: {
  projectId: string;
}): Promise<OIDCIssuerList> {
  return getAPI(`/api/projects/${projectId}/oidc-issuers`);
}

export async function createOIDCIssuer({
  projectId,
  body,
}: {
  projectId: string;
  body: CreateOIDCIssuerRequest;
}): Promise<OIDCIssuer> {
  return postAPI(`/api/projects/${projectId}/oidc-issuers`, body);
}

export async function deleteOIDCIssuer({
  projectId,
  issuerId,
}: {
  projectId: string;
  issuerId: string;
}): Promise<void> {
  await deleteAPI(`/api/projects/${projectId}/oidc-issuers/${issuerId}`);
}

export async function fetchAPIRequests({
  startsAfter = null,
  onlyFailed = false,
  apiKeyId,
  fromDateTime,
  toDateTime,
}: {
  startsAfter: string | null;
  onlyFailed: boolean;
  apiKeyId?: string;
  fromDateTime?: number;
  toDateTime?: number;
}): Promise<Page<APIRequestSummary>> {
  return getAPI(
    "/api/api-requests?" +
      buildQueryString({
        starting_after: startsAfter || undefined,
        only_failed: onlyFailed,
        api_key_id: apiKeyId || undefined,
        from_date_time: fromDateTime,
        to_date_time: toDateTime,
      })
  );
}

export async function fetchSessionAPIRequests({
  sessionId,
  startsAfter = null,
  onlyFailed = false,
  agentId,
  apiKeyId,
  fromDateTime,
  toDateTime,
}: {
  sessionId: string;
  startsAfter: string | null;
  onlyFailed: boolean;
  agentId?: string;
  apiKeyId?: string;
  fromDateTime?: number;
  toDateTime?: number;
}): Promise<Page<APIRequestSummary>> {
  return getAPI(
    `/api/sessions/${sessionId}/api-requests?` +
      buildQueryString({
        starting_after: startsAfter || undefined,
        only_failed: onlyFailed,
        agent_id: agentId,
        api_key_id: apiKeyId || undefined,
        from_date_time: fromDateTime,
        to_date_time: toDateTime,
      })
  );
}

export async function fetchAPIRequest(id: string): Promise<APIRequestDetails> {
  return getAPI(`/api/api-requests/${id}`);
}

export async function fetchSessions({
  startsAfter = null,
  sessionId = null,
  metadata = null,
  apiKeyId = null,
  fromDateTime = null,
  toDateTime = null,
  subject = null,
  sessionCorrelationId = null,
  minRequests = null,
}: {
  startsAfter: string | null;
  sessionId: string | null;
  metadata: string | null;
  apiKeyId: string | null;
  fromDateTime: number | null;
  toDateTime: number | null;
  subject: string | null;
  sessionCorrelationId: string | null;
  minRequests: number | null;
}): Promise<Page<SessionSummary>> {
  return getAPI(
    "/api/sessions?" +
      buildQueryString({
        starting_after: startsAfter || undefined,
        id: sessionId || undefined,
        metadata: metadata || undefined,
        api_key_id: apiKeyId || undefined,
        from_date_time: fromDateTime || undefined,
        to_date_time: toDateTime || undefined,
        subject: subject || undefined,
        session_correlation_id: sessionCorrelationId || undefined,
        min_requests: minRequests || undefined,
      })
  );
}

export function fetchSession(sessionId: string): Promise<SessionDetails> {
  return getAPI(`/api/sessions/${sessionId}`);
}

export function fetchSessionUsage(
  sessionId: string
): Promise<Array<SessionUsage>> {
  return getAPI(`/api/sessions/${sessionId}/usage`);
}

export function fetchAgentDetails({
  sessionId,
  agentId,
}: {
  sessionId: string;
  agentId: string;
}): Promise<AgentDetails> {
  return getAPI(`/api/sessions/${sessionId}/agents/${agentId}`);
}

export function fetchAgentPublicState({
  sessionId,
  agentId,
  momentId,
}: {
  sessionId: string;
  agentId: string;
  momentId?: number;
}): Promise<AgentState> {
  return momentId !== undefined
    ? getAPI(`/api/sessions/${sessionId}/agents/${agentId}/state/${momentId}`)
    : getAPI(`/api/sessions/${sessionId}/agents/${agentId}/state`);
}

export function fetchAgentPresentation({
  sessionId,
  agentId,
  momentId,
}: {
  sessionId: string;
  agentId: string;
  momentId?: number;
}): Promise<AgentPresentation> {
  return momentId !== undefined
    ? getAPI(
        `/api/sessions/${sessionId}/agents/${agentId}/presentation/${momentId}`
      )
    : getAPI(`/api/sessions/${sessionId}/agents/${agentId}/presentation`);
}

export function fetchOrganizationUsageRecent(): Promise<MetricUsage[]> {
  return getAPI("/api/organization/usage");
}

export async function fetchOrganizationUsageBetween({
  startDate,
  endDate,
}: {
  organization: string;
  startDate: string;
  endDate: string;
}): Promise<MetricUsage[]> {
  return getAPI(
    `/api/organization/usage?${buildQueryString({
      between: `daily-${startDate},daily-${endDate}`,
    })}`
  );
}

export function fetchOrganizationLimits(): Promise<Limits> {
  return getAPI("/api/organization/limits");
}

export function fetchUsageMetrics(): Promise<UsageMetricMetadata[]> {
  return getAPI("/api/usage-metrics");
}

export function fetchProjectUsageRecent({
  projectId,
}: {
  projectId: string;
}): Promise<MetricUsage[]> {
  return getAPI(`/api/projects/${projectId}/usage`);
}

export async function fetchProjectUsageBetween({
  projectId,
  startDate,
  endDate,
}: {
  projectId: string;
  startDate: string;
  endDate: string;
}): Promise<MetricUsage[]> {
  return getAPI(
    `/api/projects/${projectId}/usage?${buildQueryString({
      between: `daily-${startDate},daily-${endDate}`,
    })}`
  );
}

export function fetchProjectLimits(
  projectId: string
): Promise<ProjectQuotaLimits> {
  return getAPI(`/api/projects/${projectId}/limits`);
}

export function fetchEventStream({
  sessionId,
  agentId,
  reverse = false,
  startsAfter = null,
}: {
  sessionId: string;
  agentId: string;
  reverse?: boolean;
  startsAfter?: string | null;
}): Promise<Page<MessageStreamMomentSummary>> {
  return getAPI(
    `/api/sessions/${sessionId}/agents/${agentId}/event-stream?` +
      buildQueryString({ reverse, starting_after: startsAfter || undefined })
  );
}

export function fetchEventStreamDetail(
  sessionId: string,
  agentId: string,
  momentId: number
): Promise<MessageStreamMomentDetail> {
  return getAPI(
    `/api/sessions/${sessionId}/agents/${agentId}/event-stream/${momentId}`
  );
}

export function fetchMessageTypes(): Promise<MessageTypeOptions> {
  return getAPI("/api/message-types");
}

export function fetchPlaygroundSeed(
  sessionId: string,
  agentId: string,
  momentId: number
): Promise<PlaygroundSeed> {
  return getAPI(
    `/api/sessions/${sessionId}/agents/${agentId}/playground-seed/${momentId}`
  );
}

export function runPlayground(
  projectId: string,
  payload: PlaygroundRunPayload
): Promise<PlaygroundRunResult> {
  return postAPI(`/api/projects/${projectId}/playground-run`, payload);
}

export function fetchAgentMomentList({
  sessionId,
  agentId,
  startTime,
  endTime,
  functionName,
  hasError,
}: {
  sessionId: string;
  agentId: string;
  startTime?: number;
  endTime?: number;
  functionName?: string;
  hasError?: boolean;
}): Promise<AgentMomentList> {
  return getAPI(
    `/api/sessions/${sessionId}/agents/${agentId}/moments?` +
      buildQueryString({
        start_time: startTime,
        end_time: endTime,
        function_called: functionName,
        has_llm_failure: hasError,
      })
  );
}

export function fetchSessionStats({
  sessionId,
  startTime,
  endTime,
  numBuckets,
  xAxis,
}: {
  sessionId: string;
  startTime?: number;
  endTime?: number;
  numBuckets?: number;
  xAxis?: SessionXAxis;
}): Promise<SessionStats> {
  return getAPI(
    `/api/sessions/${sessionId}/stats?` +
      buildQueryString({
        start_time: startTime,
        end_time: endTime,
        num_buckets: numBuckets,
        x_axis: xAxis,
      })
  );
}

export function fetchSessionTokenUsage(
  sessionId: string
): Promise<SessionTokenUsage> {
  return getAPI(`/api/sessions/${sessionId}/token-usage`);
}

export function fetchSessionParents(
  sessionId: string
): Promise<Array<SessionRange>> {
  return getAPI(`/api/sessions/${sessionId}/parents`);
}

export function fetchSessionChildren(
  sessionId: string
): Promise<SessionTreeNode> {
  return getAPI(`/api/sessions/${sessionId}/children`);
}

export function fetchFeatureFlags(
  flagNames: Array<string>
): Promise<Record<string, boolean>> {
  return getAPI(
    "/api/feature-flags?" + buildQueryString({ flag_names: flagNames })
  );
}

export function fetchAuthorizedServiceAliases(): Promise<
  Array<AuthorizedServiceAlias>
> {
  return getAPI(`/api/authorized-aa-services`);
}

export function fetchOrganizationServices(): Promise<
  Array<OrganizationService>
> {
  return getAPI(`/api/services`);
}

export function fetchOrganizationService(
  serviceId: string
): Promise<OrganizationService> {
  return getAPI(`/api/services/${serviceId}`);
}

export function createOrganizationService(
  service: EditOrganizationService
): Promise<OrganizationService> {
  return postAPI(`/api/services`, service);
}

export function updateOrganizationService(
  service: EditOrganizationService
): Promise<void> {
  return putAPI(`/api/services/${service.uuid}`, service);
}

export function fetchUsersWidgetToken(): Promise<{ token: string }> {
  return getAPI("/api/auth/user-table-widget-token");
}

export function fetchAccountWidgetToken(): Promise<{ token: string }> {
  return getAPI("/api/auth/account-widget-token");
}

export function fetchAgentCloneTree({
  sessionId,
  agentId,
}: {
  sessionId: string;
  agentId: string;
}): Promise<AgentCloneTree> {
  return getAPI(`/api/sessions/${sessionId}/agents/${agentId}/clones`);
}

export function fetchProjectAgencyTags({
  projectId,
}: {
  projectId: string;
}): Promise<Page<AgencyTagType>> {
  return getAPI(`/api/projects/${projectId}/tags`);
}

export function createOrUpdateRating({
  sessionId,
  momentId,
  eventId,
  rating,
}: {
  sessionId: string;
  momentId: number;
  eventId: string;
  rating: RatingType;
}): Promise<RateMomentResponse> {
  const url = `/api/sessions/${sessionId}/moments/${momentId}/events/${eventId}/rating`;
  return postAPI(url, rating);
}
export function deleteRating({ eventId }: { eventId: string }): Promise<void> {
  const url = `/api/events/${eventId}/rating`;
  return deleteAPI(url);
}

export function getCommonAPI(): CommonAPI {
  return {
    createOrUpdateRating,
    deleteRating,
    fetchAgentDetails,
    fetchAgentMomentList,
    fetchAgentPublicState,
    fetchAgentPresentation,
    fetchSession,
    fetchEventStream,
    fetchEventStreamDetail,
    fetchMessageTypes,
    fetchAPIRequest: ({ requestId }) => fetchAPIRequest(requestId),
    fetchAPIKeys,
    fetchSessionStats,
    fetchSessionTokenUsage,
    fetchSessionParents,
    fetchSessionChildren,
    fetchUsersWidgetToken,
    fetchAgentCloneTree,
    fetchProjectAgencyTags,
    fetchUsageMetrics,
    fetchOrganizationUsageRecent,
    fetchProjectUsageRecent,
    fetchOrganizationUsageBetween,
    fetchProjectUsageBetween,
  };
}

// putAPI and deleteAPI widen their result to `T | undefined` because some
// endpoints answer with no body. Every entitlement endpoint answers with the
// project's status, so an empty body is a bug and should read as one here
// rather than as an undefined that spreads through the page.
function required<T>(value: T | undefined): T {
  if (value === undefined) {
    throw new Error("The entitlement API returned an empty response.");
  }
  return value;
}

// Entitlement self-service. A store credential travels one way. It
// goes up in the body of setStoreCredential and no call here returns one, not
// masked and not truncated, because the API has nothing to return it from.
export async function fetchEntitlement({
  projectId,
}: {
  projectId: string;
}): Promise<EntitlementStatus> {
  return getAPI(`/api/projects/${projectId}/entitlement`);
}

export async function enableEntitlementForProject({
  projectId,
}: {
  projectId: string;
}): Promise<EntitlementStatus> {
  return postAPI(`/api/projects/${projectId}/entitlement`, {});
}

export async function setStoreCredential({
  projectId,
  entitlementProvider,
  body,
}: {
  projectId: string;
  entitlementProvider: string;
  body: StoreCredentialRequest;
}): Promise<EntitlementStatus> {
  return required(
    await putAPI<EntitlementStatus>(
      `/api/projects/${projectId}/entitlement/stores/${entitlementProvider}`,
      body
    )
  );
}

export async function setEntitlementStores({
  projectId,
  body,
}: {
  projectId: string;
  body: SetStoresRequest;
}): Promise<EntitlementStatus> {
  return required(
    await putAPI<EntitlementStatus>(
      `/api/projects/${projectId}/entitlement/stores`,
      body
    )
  );
}

export async function goLiveWithEntitlement({
  projectId,
}: {
  projectId: string;
}): Promise<EntitlementStatus> {
  return postAPI(`/api/projects/${projectId}/entitlement/enable`, {});
}

export async function pauseEntitlement({
  projectId,
}: {
  projectId: string;
}): Promise<EntitlementStatus> {
  return postAPI(`/api/projects/${projectId}/entitlement/disable`, {});
}

export async function removeEntitlement({
  projectId,
}: {
  projectId: string;
}): Promise<EntitlementStatus> {
  return required(
    await deleteAPI<EntitlementStatus>(`/api/projects/${projectId}/entitlement`)
  );
}

export async function fetchEntitlementAudit({
  projectId,
}: {
  projectId: string;
}): Promise<EntitlementAuditList> {
  return getAPI(`/api/projects/${projectId}/entitlement/audit`);
}
